import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button,
  TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, IconButton,
  Alert, CircularProgress, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Paper
} from '@mui/material';
import { Add, Edit, ToggleOn, ToggleOff } from '@mui/icons-material';
import GroupIcon from '@mui/icons-material/Group';
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

interface Agente {
  id: number;
  nome: string;
  matricula: string;
  area: string;
  zona: string;
  ativo: boolean;
  status: string;
  criado_em?: string;
}
interface AgentesProps { onVoltar: () => void; }

const corStatus: Record<string, string> = {
  ativo: '#D1FAE5', inativo: '#FEE2E2',
  ferias: '#FEF3C7', licenca: '#EDE9FE',
};
const corTexto: Record<string, string> = {
  ativo: '#065F46', inativo: '#991B1B',
  ferias: '#92400E', licenca: '#4C1D95',
};

const fmtData = (s?: string) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const HEADER_SX = {
  backgroundColor: '#1D1D1F', color: 'white', fontWeight: 700,
  fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5, py: 1,
};

export default function Agentes({ onVoltar }: AgentesProps) {
  const { token } = useAuth();
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState<Agente | null>(null);
  const [form, setForm] = useState({ nome: '', matricula: '', area: '', zona: 'Urbana' });
  const [filtroStatus, setFiltroStatus] = useState('Todos');

  const headers = { Authorization: `Bearer ${token}` };

  const carregar = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/agentes/`, { headers });
      setAgentes(resp.data);
    } catch {
      setErro('Erro ao carregar agentes.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => {
    setEditando(null);
    setForm({ nome: '', matricula: '', area: '', zona: 'Urbana' });
    setDialogAberto(true);
  };

  const abrirEditar = (agente: Agente) => {
    setEditando(agente);
    setForm({ nome: agente.nome, matricula: agente.matricula, area: agente.area, zona: agente.zona });
    setDialogAberto(true);
  };

  const salvar = async () => {
    try {
      if (editando) {
        await axios.put(`${API_URL}/api/agentes/${editando.id}`, form, { headers });
        setSucesso('Agente atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/api/agentes/`, form, { headers });
        setSucesso('Agente cadastrado com sucesso!');
      }
      setDialogAberto(false);
      carregar();
      setTimeout(() => setSucesso(''), 3000);
    } catch (e: any) {
      setErro(e.response?.data?.detail || 'Erro ao salvar agente.');
      setTimeout(() => setErro(''), 3000);
    }
  };

  const alterarStatus = async (agente: Agente) => {
    const novoStatus = agente.status === 'ativo' ? 'inativo' : 'ativo';
    await axios.put(
      `${API_URL}/api/agentes/${agente.id}`,
      { status: novoStatus, ativo: novoStatus === 'ativo' },
      { headers }
    );
    carregar();
  };


  const agentesFiltrados = filtroStatus === 'Todos' ? agentes : agentes.filter(a => a.status === filtroStatus);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F2F2F7', display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="Agentes"
        subtitulo="Cadastro e gestão da equipe de ACEs"
        icone={<GroupIcon sx={{ fontSize: 28 }} />}
        accentColor="#0288D1"
        onVoltar={onVoltar}
      />

      <Box sx={{ p: 3, maxWidth: 1100, margin: '0 auto', width: '100%', flexGrow: 1 }}>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}
        {erro    && <Alert severity="error"   sx={{ mb: 2 }}>{erro}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            startIcon={<Add />}
            onClick={abrirNovo}
            variant="contained"
            sx={{ bgcolor: '#0071E3', '&:hover': { bgcolor: '#005BB5' }, fontWeight: 700, borderRadius: 2 }}
          >
            Novo Agente
          </Button>
        </Box>

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : agentes.length === 0 ? (
          <Alert severity="info">Nenhum agente cadastrado.</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <TextField select size="small" label="Status" value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)} sx={{ width: 200 }}>
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
                <MenuItem value="férias">Férias</MenuItem>
                <MenuItem value="licença">Licença</MenuItem>
              </TextField>
              <Typography variant="body2" color="text.secondary">
                {agentesFiltrados.length} agentes
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1D1D1F' }}>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'left' }}>Nome</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Matrícula</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Área</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Zona</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Cadastrado em</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Ações</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {agentesFiltrados.map((ag, idx) => (
                  <TableRow key={ag.id} sx={{
                    '&:hover .MuiTableCell-root': { filter: 'brightness(0.95)', transition: 'filter 0.15s' },
                  }}>
                    <TableCell sx={{ backgroundColor: '#FFFFFF', fontWeight: 700, color: '#1D1D1F', fontSize: 13, textAlign: 'left', whiteSpace: 'nowrap' }}>
                      {ag.nome}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                      {ag.matricula}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#FFFFFF', textAlign: 'center', color: '#374151' }}>
                      {ag.area}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center', color: '#374151' }}>
                      {ag.zona}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#FFFFFF', textAlign: 'center' }}>
                      <Chip
                        label={ag.status.charAt(0).toUpperCase() + ag.status.slice(1)}
                        size="small"
                        onClick={() => alterarStatus(ag)}
                        sx={{
                          bgcolor: corStatus[ag.status] || '#F3F4F6',
                          color: corTexto[ag.status] || '#374151',
                          fontWeight: 700, cursor: 'pointer',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center', color: '#6B7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {fmtData(ag.criado_em)}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#FFFFFF', textAlign: 'center' }}>
                      <IconButton onClick={() => abrirEditar(ag)} size="small" sx={{ color: '#34C759' }}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => alterarStatus(ag)} size="small" sx={{ color: ag.status === 'ativo' ? '#0071E3' : '#8E8E93' }}>
                        {ag.status === 'ativo' ? <ToggleOn /> : <ToggleOff />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow sx={{ backgroundColor: '#1D1D1F' }}>
                  <TableCell colSpan={7} sx={{ color: 'white', fontWeight: 700, fontSize: 12, textAlign: 'right', py: 1 }}>
                    Total: {agentesFiltrados.length} agentes
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          </>
        )}
      </Box>

      <Box sx={{ py: 2, textAlign: 'center', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          © 2026 Tovis · Desenvolvido e mantido por <strong style={{ color: '#6B7280' }}>Walber Reis</strong>
        </Typography>
      </Box>

      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B3A6B' }}>
          {editando ? 'Editar Agente' : 'Novo Agente'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome completo" value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            sx={{ mb: 2, mt: 1 }} required />
          <TextField fullWidth label="Matrícula" value={form.matricula}
            onChange={(e) => setForm({ ...form, matricula: e.target.value })}
            sx={{ mb: 2 }} required />
          <TextField fullWidth label="Área / Bairro" value={form.area}
            onChange={(e) => setForm({ ...form, area: e.target.value })}
            sx={{ mb: 2 }} required />
          <TextField select fullWidth label="Zona" value={form.zona}
            onChange={(e) => setForm({ ...form, zona: e.target.value })}
            sx={{ mb: 1 }}>
            <MenuItem value="Urbana">Urbana</MenuItem>
            <MenuItem value="Rural">Rural</MenuItem>
            <MenuItem value="Mista">Mista</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogAberto(false)} sx={{ color: '#6B7280' }}>
            Cancelar
          </Button>
          <Button onClick={salvar} variant="contained"
            sx={{ bgcolor: '#1B3A6B', '&:hover': { bgcolor: '#152d52' }, fontWeight: 700 }}>
            {editando ? 'Salvar alterações' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

